import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  type: 'preparation' | 'celebration' | 'community' | 'global';
  description: string;
  organizer: string;
  attendees: number;
}

interface DayViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  onEventClick: (eventId: string) => void;
}

const DayView: React.FC<DayViewProps> = ({ currentDate, events, onEventClick }) => {
  const getEventsForDate = () => {
    const dateStr = currentDate.toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'preparation':
        return 'border-l-peaceful-teal bg-peaceful-teal/10';
      case 'celebration':
        return 'border-l-accent bg-accent/10';
      case 'community':
        return 'border-l-secondary bg-secondary/10';
      case 'global':
        return 'border-l-primary bg-primary/10';
      default:
        return 'border-l-muted bg-muted/10';
    }
  };

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'preparation':
        return 'Preparation';
      case 'celebration':
        return 'Celebration';
      case 'community':
        return 'Community';
      case 'global':
        return 'Global Unity';
      default:
        return 'Event';
    }
  };

  const dayEvents = getEventsForDate();
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="bg-card rounded-lg shadow-sm overflow-hidden">
      {/* Day header */}
      <div className="p-6 bg-muted border-b border-border">
        <h3 className="text-2xl font-headline font-bold text-foreground">
          {currentDate.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'} scheduled
        </p>
      </div>

      {/* Timeline */}
      <div className="overflow-y-auto max-h-[600px]">
        {hours.map((hour) => {
          const hourEvents = dayEvents.filter(event => {
            const eventHour = parseInt(event.time.split(':')[0]);
            return eventHour === hour;
          });

          return (
            <div key={hour} className="flex border-b border-border min-h-24">
              <div className="w-20 p-4 bg-muted text-sm text-muted-foreground text-right flex-shrink-0">
                {hour.toString().padStart(2, '0')}:00
              </div>
              <div className="flex-1 p-4">
                {hourEvents.length > 0 ? (
                  <div className="space-y-3">
                    {hourEvents.map((event) => (
                      <button
                        key={event.id}
                        onClick={() => onEventClick(event.id)}
                        className={`w-full text-left p-4 rounded-lg border-l-4 ${getEventTypeColor(
                          event.type
                        )} hover:shadow-md transition-all duration-200`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-headline font-bold text-foreground mb-1">
                              {event.title}
                            </h4>
                            <span className="text-xs font-cta font-bold text-primary">
                              {getEventTypeLabel(event.type)}
                            </span>
                          </div>
                          <span className="text-sm font-body text-muted-foreground">
                            {event.time}
                          </span>
                        </div>
                        <p className="text-sm text-foreground mb-3 line-clamp-2">
                          {event.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Icon name="MapPinIcon" size={14} />
                            <span>{event.location}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="UserGroupIcon" size={14} />
                            <span>{event.attendees} attending</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                    No events scheduled
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DayView;